import { Component, HostListener, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwAvatar } from '../../shared/ui/avatar/uw-avatar';
import { UwKbd } from '../../shared/ui/kbd/uw-kbd';
import { UwNotificationsDropdown } from '../notifications/uw-notifications-dropdown';
import { WorkbenchStore } from '../../core/stores/workbench.store';
import { CommandService } from '../../core/services/command.service';
import { ThemeService } from '../../core/services/theme.service';
import { NotificationService } from '../../core/services/notification.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/auth/auth.service';

const THEME_ICONS = { light: 'sun', dark: 'moon', ai: 'sparkles', system: 'sun-moon' } as const;

@Component({
  selector: 'uw-topbar',
  standalone: true,
  imports: [UwIcon, UwAvatar, UwKbd, UwNotificationsDropdown],
  template: `
    <header class="relative z-header flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3 sm:px-4">
      <button
        class="inline-flex items-center justify-center rounded-md p-2 text-secondary transition-colors hover:bg-surface-sunken lg:hidden"
        (click)="workbench.setMobileNav(true)"
        aria-label="Open navigation"
      >
        <uw-icon name="menu" [size]="18" />
      </button>

      <div class="hidden items-center gap-1.5 lg:flex" role="tablist" aria-label="Workbench mode">
        <button
          role="tab"
          [attr.aria-selected]="workbench.mode() === 'copilot'"
          class="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors"
          [class]="workbench.mode() === 'copilot' ? 'bg-ai-soft text-ai' : 'text-secondary hover:bg-surface-sunken'"
          (click)="switchMode('copilot')"
        >
          <uw-icon name="sparkles" [size]="13" />
          AI Copilot
        </button>
        <button
          role="tab"
          [attr.aria-selected]="workbench.mode() === 'classic'"
          class="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors"
          [class]="workbench.mode() === 'classic' ? 'bg-primary-soft text-primary' : 'text-secondary hover:bg-surface-sunken'"
          (click)="switchMode('classic')"
        >
          <uw-icon name="layout-grid" [size]="13" />
          Classic
        </button>
      </div>

      <button
        class="ml-2 hidden flex-1 items-center gap-2 rounded-lg border border-line bg-surface-sunken/60 px-3 py-1.5 text-left text-[12px] text-text-muted transition-colors hover:border-line-strong hover:text-secondary sm:flex sm:max-w-md"
        (click)="command.openPalette()"
        aria-label="Search or type a command"
      >
        <uw-icon name="search" [size]="14" />
        <span class="flex-1">Search or type a command…</span>
        <uw-kbd>⌘K</uw-kbd>
      </button>

      <div class="ml-auto flex items-center gap-1">
        <button
          class="inline-flex items-center justify-center rounded-md p-2 text-secondary transition-colors hover:bg-surface-sunken"
          (click)="cycleTheme()"
          [attr.title]="'Theme: ' + theme.theme()"
          [attr.aria-label]="'Toggle theme (' + theme.theme() + ')'"
        >
          <uw-icon [name]="THEME_ICONS[theme.theme()]" [size]="17" />
        </button>

        <div class="relative">
          <button
            class="relative inline-flex items-center justify-center rounded-md p-2 text-secondary transition-colors hover:bg-surface-sunken"
            (click)="toggleNotifications($event)"
            aria-label="Notifications"
          >
            <uw-icon name="bell" [size]="17" />
            @if (notificationService.unreadCount() > 0) {
              <span class="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-ai px-1 text-[9px] font-bold text-white">
                {{ notificationService.unreadCount() }}
              </span>
            }
          </button>
          @if (notificationsOpen()) {
            <uw-notifications-dropdown (closed)="notificationsOpen.set(false)" />
          }
        </div>

        <div class="relative">
          <button
            class="ml-1 flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-surface-sunken"
            (click)="toggleUserMenu($event)"
            aria-label="User menu"
          >
            <uw-avatar [initials]="user.initials" [label]="user.name" size="sm" tone="accent" />
            <span class="hidden text-left md:block">
              <span class="block text-[11.5px] font-semibold leading-tight text-primary">{{ user.name }}</span>
              <span class="block text-[10px] leading-tight text-text-muted">{{ user.role }}</span>
            </span>
            <uw-icon name="chevron-down" [size]="12" class="text-text-muted" />
          </button>

          @if (userMenuOpen()) {
            <div class="absolute right-0 top-[calc(100%+10px)] z-popover w-64 overflow-hidden rounded-xl border border-line bg-surface-elevated shadow-lg">
              <div class="border-b border-line px-4 py-3">
                <p class="text-[13px] font-semibold text-primary">{{ user.name }}</p>
                <p class="text-[11px] text-text-muted">{{ user.email }}</p>
              </div>
              <div class="p-1.5">
                <p class="typo-label px-2 pb-1 pt-1.5">Appearance</p>
                @for (option of themeOptions; track option.id) {
                  <button
                    class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] transition-colors hover:bg-surface-sunken"
                    [class.font-semibold]="theme.theme() === option.id"
                    [class.text-ai]="theme.theme() === option.id"
                    [class.text-secondary]="theme.theme() !== option.id"
                    (click)="theme.setTheme(option.id)"
                  >
                    <uw-icon [name]="option.icon" [size]="14" class="text-text-muted" />
                    {{ option.label }}
                  </button>
                }
                <div class="my-1.5 border-t border-line"></div>
                <button
                  class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12px] text-secondary transition-colors hover:bg-surface-sunken hover:text-danger"
                  (click)="logout()"
                >
                  <uw-icon name="logout" [size]="14" class="text-text-muted" />
                  Sign out
                </button>
              </div>
            </div>
          }
        </div>
      </div>
    </header>
  `,
})
export class UwTopbar {
  private readonly auth = inject(AuthService);
  protected readonly THEME_ICONS = THEME_ICONS;
  protected get user() {
    return this.auth.currentUser();
  }

  protected readonly themeOptions = [
    { id: 'light', label: 'Light', icon: 'sun' },
    { id: 'dark', label: 'Dark', icon: 'moon' },
    { id: 'ai', label: 'AI', icon: 'sparkles' },
    { id: 'system', label: 'System', icon: 'sun-moon' },
  ] as const;

  protected readonly workbench = inject(WorkbenchStore);
  protected readonly command = inject(CommandService);
  protected readonly theme = inject(ThemeService);
  protected readonly notificationService = inject(NotificationService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  protected readonly notificationsOpen = signal(false);
  protected readonly userMenuOpen = signal(false);

  @HostListener('document:click')
  protected onDocumentClick(): void {
    this.notificationsOpen.set(false);
    this.userMenuOpen.set(false);
  }

  protected toggleNotifications(event?: MouseEvent): void {
    event?.stopPropagation();
    this.userMenuOpen.set(false);
    this.notificationsOpen.update((v) => !v);
  }

  protected toggleUserMenu(event?: MouseEvent): void {
    event?.stopPropagation();
    this.notificationsOpen.set(false);
    this.userMenuOpen.update((v) => !v);
  }

  protected cycleTheme(): void {
    const next = this.theme.cycle();
    this.toast.info('Theme updated', `Switched to ${next} theme.`);
  }

  protected switchMode(mode: 'copilot' | 'classic'): void {
    this.workbench.setMode(mode);
    void this.router.navigate(mode === 'copilot' ? ['/workbench'] : ['/classic/submissions']);
  }

  protected logout(): void {
    this.auth.logout({ redirectToLogin: true, reason: 'signed-out' });
  }
}
